import { create } from 'zustand';
import { getBranches } from '../../services/patients/patientsService';

export type DistributionCenterItem = {
    label: string;
    value: string;
};

type BranchApiRow = {
    id: number | string;
    name?: string;
    description?: string;
};

type BranchesResponse =
    | BranchApiRow[]
    | { data?: BranchApiRow[] }
    | null
    | undefined;

type DistributionCentersState = {
    items: DistributionCenterItem[];
    byCode: Record<string, string>;
    loading: boolean;
    loaded: boolean;
    fetch: () => Promise<void>;
};

const EMPTY_ITEM: DistributionCenterItem = { label: ' - ', value: 'none' };

const normalize = (v: unknown) => String(v ?? '').trim();

const extractBranchList = (rows: BranchesResponse): BranchApiRow[] => {
    if (Array.isArray(rows)) return rows;
    if (rows && typeof rows === 'object' && Array.isArray(rows.data)) {
        return rows.data;
    }
    return [];
};

export const useDistributionCentersStore = create<DistributionCentersState>(
    (set, get) => ({
        items: [],
        byCode: {},
        loading: false,
        loaded: false,
        fetch: async () => {
            const { loaded, loading } = get();
            if (loaded || loading) return;

            set({ loading: true });
            try {
                const rows = (await getBranches()) as BranchesResponse;
                const list = extractBranchList(rows);

                const items: DistributionCenterItem[] = list
                    .map((r: BranchApiRow) => {
                        const value = normalize(r.id);
                        const label = normalize(r.name ?? r.description);
                        if (!value || !label) return null;
                        return { value, label };
                    })
                    .filter(Boolean) as DistributionCenterItem[];

                // Keep compatibility with old UI that used 'none' as empty option.
                items.unshift(EMPTY_ITEM);

                const byCode: Record<string, string> = {};
                for (const it of items) byCode[it.value] = it.label;

                set({ items, byCode, loaded: true, loading: false });
            } catch (e) {
                console.error('[distributionCentersStore] Failed to fetch branches', e);
                set({ loading: false, loaded: false });
            }
        },
    })
);
